import React, { useState, useEffect } from 'react';
import Layout from '../components/Layout';
import { getBOMs, createBOM, deleteBOM, getSkus, getVariants, getProductionOrders, createProductionOrder, advancePOStage, getBOMCost, getLocations } from '../lib/api';
import { Factory, Plus, Trash, Play, CheckCircle, Warning, Calculator, Camera, MapPin } from '@phosphor-icons/react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { FractionalQtyInput, formatQty } from '../components/FractionalQtyInput';
import { BOMTreeTab, WhereUsedTab, MRPExplosionTab } from '../components/AdvancedBomTabs';
import ConsolidatedDraftsTab from '../components/ConsolidatedDraftsTab';
import ProductionScanDialog from '../components/ProductionScanDialog';
import SkuVariantPicker from '../components/SkuVariantPicker';
import { toast } from 'sonner';

const stageColors = {
  raw_material: 'bg-muted text-slate-800',
  assembly: 'bg-blue-100 text-blue-800',
  qa: 'bg-amber-100 text-amber-800',
  finished_good: 'bg-green-100 text-green-800'
};

export default function Manufacturing() {
  const [boms, setBoms] = useState([]);
  const [productionOrders, setProductionOrders] = useState([]);
  const [skus, setSkus] = useState([]);
  const [variants, setVariants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateBom, setShowCreateBom] = useState(false);
  const [showCreatePO, setShowCreatePO] = useState(false);

  const [newBom, setNewBom] = useState({
    finished_good_sku_id: '', name: '', version: '1.0', yield_pct: 100, is_phantom: false,
    components: [{ component_type: 'raw', variant_id: '', child_bom_id: '', quantity: 1, scrap_pct: 0, notes: '' }]
  });
  const [newPO, setNewPO] = useState({ bom_id: '', quantity: 1, notes: '' });
  const [bomCost, setBomCost] = useState(null);
  const [showCostModal, setShowCostModal] = useState(false);
  const [costMethod, setCostMethod] = useState('most_recent');
  const [costBomId, setCostBomId] = useState(null);
  // Production scan-to-count dialog — holds the PO being scanned against
  const [scanPO, setScanPO] = useState(null);
  // Completion put-away dialog — holds the PO being completed and the picked location.
  const [completePO, setCompletePO] = useState(null);
  const [completeLocationId, setCompleteLocationId] = useState('');
  const [completeSearch, setCompleteSearch] = useState('');
  const [locations, setLocations] = useState([]);
  const [locationsLoading, setLocationsLoading] = useState(false);
  const [completing, setCompleting] = useState(false);

  const fetchData = async () => {
    try {
      const [bomsRes, poRes, skuRes, varRes] = await Promise.all([
        getBOMs(), getProductionOrders(), getSkus(), getVariants()
      ]);
      setBoms(bomsRes.data);
      setProductionOrders(poRes.data);
      setSkus(skuRes.data);
      setVariants(varRes.data);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchData(); }, []);

  // iter149 — variantId → SKU doc lookup so the BOM component qty input
  // can render per-SKU fractional rules + UoM suffix.
  const variantSkuMap = React.useMemo(() => {
    const skuById = Object.fromEntries((skus || []).map((s) => [s.id, s]));
    const map = {};
    for (const v of (variants || [])) {
      const sku = skuById[v.sku_id];
      if (sku) map[v.id] = sku;
    }
    return map;
  }, [skus, variants]);

  const addComponent = () => {
    setNewBom({ ...newBom, components: [...newBom.components, { component_type: 'raw', variant_id: '', child_bom_id: '', quantity: 1, scrap_pct: 0, notes: '' }] });
  };

  const updateComponent = (idx, field, value) => {
    const updated = [...newBom.components];
    updated[idx][field] = value;
    setNewBom({ ...newBom, components: updated });
  };

  const removeComponent = (idx) => {
    setNewBom({ ...newBom, components: newBom.components.filter((_, i) => i !== idx) });
  };

  const handleCreateBom = async (e) => {
    e.preventDefault();
    try {
      // Clean payload: strip fields irrelevant to the selected component_type
      const cleaned = {
        ...newBom,
        components: newBom.components.map(c => {
          if (c.component_type === 'bom') {
            return {
              component_type: 'bom',
              child_bom_id: c.child_bom_id,
              quantity: Number(c.quantity) || 1,
              scrap_pct: Number(c.scrap_pct) || 0,
              notes: c.notes,
            };
          }
          return {
            component_type: 'raw',
            variant_id: c.variant_id,
            quantity: Number(c.quantity) || 1,
            scrap_pct: Number(c.scrap_pct) || 0,
            notes: c.notes,
          };
        }),
      };
      await createBOM(cleaned);
      setShowCreateBom(false);
      setNewBom({ finished_good_sku_id: '', name: '', version: '1.0', yield_pct: 100, is_phantom: false, components: [{ component_type: 'raw', variant_id: '', child_bom_id: '', quantity: 1, scrap_pct: 0, notes: '' }] });
      fetchData();
    } catch (e) { console.error(e); }
  };

  const handleCreatePO = async (e) => {
    e.preventDefault();
    try {
      await createProductionOrder(newPO);
      setShowCreatePO(false);
      setNewPO({ bom_id: '', quantity: 1, notes: '' });
      fetchData();
    } catch (e) { console.error(e); }
  };

  const handleAdvance = async (po) => {
    // The terminal advance (finished_good stage → completed) is where the
    // finished goods actually come to life as stock. Ask the operator where
    // to put them.
    if (po?.stage === 'finished_good' && po?.status !== 'completed') {
      setCompletePO(po);
      setCompleteLocationId('');
      setCompleteSearch('');
      // Lazy-load locations only when the dialog opens
      if (locations.length === 0 && !locationsLoading) {
        setLocationsLoading(true);
        try {
          const r = await getLocations({ is_active: true, limit: 500 });
          const list = Array.isArray(r.data) ? r.data : (r.data?.items || []);
          setLocations(list);
        } catch (e) { console.error(e); }
        finally { setLocationsLoading(false); }
      }
      return;
    }
    try { await advancePOStage(po.id); fetchData(); } catch (e) { console.error(e); }
  };

  const handleCompleteWithLocation = async () => {
    if (!completePO) return;
    if (!completeLocationId) {
      toast.error('Pick a location for the finished goods');
      return;
    }
    setCompleting(true);
    try {
      const r = await advancePOStage(completePO.id, { location_id: completeLocationId });
      const locCode = r?.data?.location_code || locations.find((l) => l.id === completeLocationId)?.location_code;
      toast.success(`Production complete · ${r?.data?.finished_goods_created ?? completePO.quantity} unit(s) put away at ${locCode || 'location'}`);
      setCompletePO(null);
      fetchData();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Complete failed');
    } finally {
      setCompleting(false);
    }
  };

  const handleDeleteBom = async (id) => {
    if (window.confirm('Deactivate this BOM?')) {
      await deleteBOM(id);
      fetchData();
    }
  };

  const handleShowCost = async (id, method = costMethod) => {
    try {
      const res = await getBOMCost(id, method);
      setBomCost(res.data);
      setCostBomId(id);
      setShowCostModal(true);
    } catch (e) {
      console.error(e);
    }
  };

  const handleChangeCostMethod = async (method) => {
    setCostMethod(method);
    if (costBomId) {
      await handleShowCost(costBomId, method);
    }
  };

  return (
    <Layout>
      <div data-testid="manufacturing-page" className="p-6">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-6">
          <div>
            <h1 className="text-3xl font-bold tracking-tight">Manufacturing</h1>
            <p className="text-muted-foreground">Bill of Materials & production orders</p>
          </div>
          <div className="flex gap-2">
            <Dialog open={showCreateBom} onOpenChange={setShowCreateBom}>
              <DialogTrigger asChild>
                <Button variant="outline" data-testid="create-bom-btn">
                  <Plus size={18} className="mr-2" /> New BOM
                </Button>
              </DialogTrigger>
              <DialogContent className="border border-border rounded-none max-w-2xl max-h-[90vh] overflow-y-auto">
                <DialogHeader>
                  <DialogTitle>Create Bill of Materials</DialogTitle>
                </DialogHeader>
                <form onSubmit={handleCreateBom} className="space-y-4">
                  <div>
                    <Label className="overline">BOM Name</Label>
                    <Input value={newBom.name} onChange={(e) => setNewBom({...newBom, name: e.target.value})} required className="mt-1" />
                  </div>
                  <div>
                    <Label className="overline">Finished Good SKU</Label>
                    <div className="mt-1">
                      <SkuVariantPicker
                        skus={skus}
                        variants={variants}
                        value={newBom.finished_good_sku_id}
                        onChange={(skuId) => setNewBom({...newBom, finished_good_sku_id: skuId})}
                        mode="sku"
                        testIdPrefix="bom-finished-good"
                        placeholder="Search SKU code or name…"
                      />
                    </div>
                  </div>
                  <div className="grid grid-cols-3 gap-3">
                    <div>
                      <Label className="overline">Version</Label>
                      <Input value={newBom.version} onChange={(e) => setNewBom({...newBom, version: e.target.value})} className="mt-1" />
                    </div>
                    <div>
                      <Label className="overline">Yield %</Label>
                      <Input
                        type="number" min={1} max={100}
                        value={newBom.yield_pct}
                        onChange={(e) => setNewBom({...newBom, yield_pct: parseFloat(e.target.value) || 100})}
                        className="mt-1"
                        data-testid="bom-yield-pct"
                      />
                      <div className="text-[10px] opacity-60 font-mono mt-0.5">95% = MRP inflates qty 5% to cover waste</div>
                    </div>
                    <div>
                      <Label className="overline">Phantom</Label>
                      <label className="flex items-center gap-2 mt-2 text-sm cursor-pointer">
                        <input
                          type="checkbox"
                          checked={!!newBom.is_phantom}
                          onChange={(e) => setNewBom({...newBom, is_phantom: e.target.checked})}
                          data-testid="bom-is-phantom"
                        />
                        <span className="text-xs">Hoist components into parent (not stocked)</span>
                      </label>
                    </div>
                  </div>
                  <div>
                    <div className="flex items-center justify-between mb-2">
                      <Label className="overline">Components</Label>
                      <Button type="button" size="sm" variant="outline" onClick={addComponent}>
                        <Plus size={14} className="mr-1" /> Add Component
                      </Button>
                    </div>
                    <div className="space-y-2">
                      {newBom.components.map((comp, idx) => (
                        <div key={idx} className="border border-border p-2 space-y-2">
                          <div className="grid grid-cols-12 gap-2 items-end">
                            <div className="col-span-3">
                              <Label className="text-[9px] uppercase opacity-60">Type</Label>
                              <Select value={comp.component_type || 'raw'} onValueChange={(v) => updateComponent(idx, 'component_type', v)}>
                                <SelectTrigger data-testid={`comp-type-${idx}`}>
                                  <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                  <SelectItem value="raw">Raw component</SelectItem>
                                  <SelectItem value="bom">Sub-assembly (BOM)</SelectItem>
                                </SelectContent>
                              </Select>
                            </div>
                            <div className="col-span-5">
                              <Label className="text-[9px] uppercase opacity-60">
                                {comp.component_type === 'bom' ? 'Child BOM' : 'Variant'}
                              </Label>
                              {comp.component_type === 'bom' ? (
                                <Select value={comp.child_bom_id} onValueChange={(v) => updateComponent(idx, 'child_bom_id', v)}>
                                  <SelectTrigger data-testid={`comp-child-bom-${idx}`}>
                                    <SelectValue placeholder="Select sub-assembly BOM" />
                                  </SelectTrigger>
                                  <SelectContent>
                                    {boms.map(b => (
                                      <SelectItem key={b.id} value={b.id}>
                                        {b.bom_number} · {b.name}
                                      </SelectItem>
                                    ))}
                                  </SelectContent>
                                </Select>
                              ) : (
                                <SkuVariantPicker
                                  skus={skus}
                                  variants={variants}
                                  value={comp.variant_id}
                                  onChange={(vid) => updateComponent(idx, 'variant_id', vid)}
                                  testIdPrefix={`comp-variant-picker-${idx}`}
                                  placeholder="Search variant…"
                                />
                              )}
                            </div>
                            <div className="col-span-2">
                              <Label className="text-[9px] uppercase opacity-60">Qty{(() => { const s = variantSkuMap[comp.variant_id]; return s?.allow_fractional && s?.unit_of_measure && s.unit_of_measure !== 'each' ? ` (${s.unit_of_measure})` : ''; })()}</Label>
                              <FractionalQtyInput
                                sku={variantSkuMap[comp.variant_id]}
                                value={comp.quantity}
                                onChange={(num) => updateComponent(idx, 'quantity', num)}
                                data-testid={`bom-comp-qty-${idx}`}
                              />
                            </div>
                            <div className="col-span-1">
                              <Label className="text-[9px] uppercase opacity-60">Scrap%</Label>
                              <Input type="number" min={0} max={100} step={0.5} value={comp.scrap_pct || 0} onChange={(e) => updateComponent(idx, 'scrap_pct', parseFloat(e.target.value) || 0)} data-testid={`comp-scrap-${idx}`} />
                            </div>
                            <div className="col-span-1">
                              <Button type="button" variant="outline" size="sm" onClick={() => removeComponent(idx)} className="w-full text-red-600 border-red-200">
                                <Trash size={14} />
                              </Button>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                  <Button type="submit" className="w-full bg-brand hover:bg-brand/90">Create BOM</Button>
                </form>
              </DialogContent>
            </Dialog>
            <Dialog open={showCreatePO} onOpenChange={setShowCreatePO}>
              <DialogTrigger asChild>
                <Button data-testid="create-po-btn" className="bg-brand hover:bg-brand/90">
                  <Plus size={18} className="mr-2" /> Production Order
                </Button>
              </DialogTrigger>
              <DialogContent className="border border-border rounded-none">
                <DialogHeader>
                  <DialogTitle>Create Production Order</DialogTitle>
                </DialogHeader>
                <form onSubmit={handleCreatePO} className="space-y-4">
                  <div>
                    <Label className="overline">BOM</Label>
                    <Select value={newPO.bom_id} onValueChange={(v) => setNewPO({...newPO, bom_id: v})}>
                      <SelectTrigger className="mt-1"><SelectValue placeholder="Select BOM" /></SelectTrigger>
                      <SelectContent>
                        {boms.map(b => <SelectItem key={b.id} value={b.id}>{b.bom_number} - {b.name}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="overline">Quantity to Produce</Label>
                    <Input type="number" min={1} value={newPO.quantity} onChange={(e) => setNewPO({...newPO, quantity: parseInt(e.target.value)})} required className="mt-1" />
                  </div>
                  <div>
                    <Label className="overline">Notes</Label>
                    <Input value={newPO.notes} onChange={(e) => setNewPO({...newPO, notes: e.target.value})} className="mt-1" />
                  </div>
                  <Button type="submit" className="w-full bg-brand hover:bg-brand/90">Create Order</Button>
                </form>
              </DialogContent>
            </Dialog>
          </div>
        </div>

        <Tabs defaultValue="production" className="bg-card border border-border rounded-lg">
          <div className="overflow-x-auto">
          <TabsList className="w-full border-b border-border p-0 h-auto bg-transparent rounded-none flex-nowrap min-w-max">
            <TabsTrigger value="production" data-testid="tab-production-orders" className="flex-1 rounded-none data-[state=active]:bg-muted data-[state=active]:border-b-2 data-[state=active]:border-brand">Production Orders ({productionOrders.length})</TabsTrigger>
            <TabsTrigger value="boms" data-testid="tab-boms" className="flex-1 rounded-none data-[state=active]:bg-muted data-[state=active]:border-b-2 data-[state=active]:border-brand">Bills of Materials ({boms.length})</TabsTrigger>
            <TabsTrigger value="tree" data-testid="tab-bom-tree" className="flex-1 rounded-none data-[state=active]:bg-muted data-[state=active]:border-b-2 data-[state=active]:border-brand">BOM Tree</TabsTrigger>
            <TabsTrigger value="where-used" data-testid="tab-where-used" className="flex-1 rounded-none data-[state=active]:bg-muted data-[state=active]:border-b-2 data-[state=active]:border-brand">Where Used</TabsTrigger>
            <TabsTrigger value="mrp" data-testid="tab-mrp" className="flex-1 rounded-none data-[state=active]:bg-muted data-[state=active]:border-b-2 data-[state=active]:border-brand">MRP Explosion</TabsTrigger>
            <TabsTrigger value="consolidate" data-testid="tab-consolidate" className="flex-1 rounded-none data-[state=active]:bg-muted data-[state=active]:border-b-2 data-[state=active]:border-brand">Consolidated Drafts</TabsTrigger>
          </TabsList>
          </div>

          <TabsContent value="production" className="p-0 m-0">
            <div className="p-4 grid gap-4">
              {loading ? (
                <p className="text-center py-8 text-muted-foreground">Loading...</p>
              ) : productionOrders.length === 0 ? (
                <div className="text-center py-12">
                  <Factory size={48} className="mx-auto mb-4 text-slate-300" />
                  <p className="text-muted-foreground">No production orders</p>
                </div>
              ) : productionOrders.map(po => (
                <div key={po.id} className="border border-border p-4">
                  <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1 flex-wrap">
                        <span className="font-mono font-bold">{po.production_order_number}</span>
                        <span className={`px-2 py-0.5 text-xs font-bold uppercase ${stageColors[po.stage] || 'bg-muted'}`}>{po.stage?.replace('_', ' ')}</span>
                        {po.status === 'blocked' && (
                          <span className="px-2 py-0.5 text-xs font-bold uppercase bg-red-100 text-red-800">
                            <Warning size={12} className="inline mr-1" /> Blocked
                          </span>
                        )}
                        {po.status === 'completed' && (
                          <span className="px-2 py-0.5 text-xs font-bold uppercase bg-green-100 text-green-800">
                            <CheckCircle size={12} className="inline mr-1" /> Done
                          </span>
                        )}
                      </div>
                      <p className="text-sm text-muted-foreground">{po.finished_good_name} x {po.quantity}</p>
                      <p className="text-xs text-muted-foreground">BOM: {po.bom_name}</p>
                    </div>
                    {po.status !== 'completed' && po.status !== 'blocked' && (
                      <div className="flex gap-2 flex-wrap">
                        <Button
                          onClick={() => setScanPO(po)}
                          data-testid={`scan-po-${po.production_order_number}`}
                          variant="outline"
                          className="border-brand text-brand hover:bg-brand/5"
                          title="Scan-to-count consume/yield"
                        >
                          <Camera size={16} className="mr-2" weight="fill" /> Scan
                        </Button>
                        <Button
                          onClick={() => handleAdvance(po)}
                          className="bg-brand hover:bg-brand/90"
                          data-testid={`advance-po-${po.production_order_number}`}
                        >
                          {po.stage === 'finished_good' && po.status !== 'completed' ? (
                            <><CheckCircle size={16} className="mr-2" weight="fill" /> Complete &amp; put away</>
                          ) : (
                            <><Play size={16} className="mr-2" /> Advance Stage</>
                          )}
                        </Button>
                      </div>
                    )}
                  </div>
                  {po.components_availability && (
                    <div className="mt-3 pt-3 border-t border-slate-100">
                      <p className="overline mb-2">Components</p>
                      <div className="space-y-1">
                        {po.components_availability.map((c, i) => (
                          <div key={i} className="flex items-center gap-2 text-xs font-mono">
                            <span className={c.sufficient ? 'text-green-600' : 'text-red-600'}>
                              {c.sufficient ? '✓' : '✗'}
                            </span>
                            <span>{c.variant_id?.slice(-8)}</span>
                            <span>needed: {c.needed}</span>
                            <span>available: {c.available}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </TabsContent>

          <TabsContent value="boms" className="p-0 m-0">
            <div className="overflow-x-auto">
              <table className="w-full table-dense">
                <thead>
                  <tr>
                    <th>BOM #</th>
                    <th>Name</th>
                    <th>Finished Good</th>
                    <th>Version</th>
                    <th>Components</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {boms.length === 0 ? (
                    <tr><td colSpan={6} className="text-center py-8 text-muted-foreground">No BOMs</td></tr>
                  ) : boms.map(b => (
                    <tr key={b.id}>
                      <td className="font-mono font-semibold">{b.bom_number}</td>
                      <td>{b.name}</td>
                      <td>{b.finished_good_name}</td>
                      <td className="font-mono">{b.version}</td>
                      <td className="font-mono">{b.components?.length || 0}</td>
                      <td>
                        <div className="flex gap-1">
                          <button onClick={() => handleShowCost(b.id)} className="p-1.5 hover:bg-blue-50 text-blue-600" title="Calculate Cost" data-testid={`bom-cost-${b.id}`}>
                            <Calculator size={16} />
                          </button>
                          <button onClick={() => handleDeleteBom(b.id)} className="p-1.5 hover:bg-red-50 text-red-600" title="Deactivate">
                            <Trash size={16} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </TabsContent>

          <TabsContent value="tree" className="p-0 m-0">
            <BOMTreeTab boms={boms} />
          </TabsContent>

          <TabsContent value="where-used" className="p-0 m-0">
            <WhereUsedTab />
          </TabsContent>

          <TabsContent value="mrp" className="p-0 m-0">
            <MRPExplosionTab boms={boms} />
          </TabsContent>

          <TabsContent value="consolidate" className="p-0 m-0">
            <ConsolidatedDraftsTab />
          </TabsContent>
        </Tabs>

        {/* BOM Cost Modal */}
        <Dialog open={showCostModal} onOpenChange={setShowCostModal}>
          <DialogContent className="border border-border rounded-none" data-testid="bom-cost-modal">
            <DialogHeader>
              <DialogTitle>Cost Breakdown</DialogTitle>
            </DialogHeader>
            {bomCost && (
              <div>
                <div className="mb-4">
                  <label className="overline block mb-2">Cost Method</label>
                  <div className="grid grid-cols-3 gap-2">
                    {[
                      { key: 'most_recent', label: 'Most Recent' },
                      { key: 'fifo', label: 'FIFO' },
                      { key: 'weighted_avg', label: 'Weighted Avg' }
                    ].map(({ key, label }) => (
                      <button
                        key={key}
                        onClick={() => handleChangeCostMethod(key)}
                        data-testid={`cost-method-${key}`}
                        className={`px-3 py-2 text-xs font-bold uppercase border transition-colors ${
                          costMethod === key
                            ? 'bg-brand text-white border-brand'
                            : 'bg-card text-muted-foreground border-border hover:bg-muted'
                        }`}
                      >
                        {label}
                      </button>
                    ))}
                  </div>
                </div>
                <div className="bg-brand text-white p-5 mb-4">
                  <p className="text-xs uppercase font-bold tracking-widest opacity-70">{bomCost.bom_name}</p>
                  <p className="text-sm mb-2">{bomCost.finished_good_name}</p>
                  <p className="text-4xl font-bold">
                    ${bomCost.total_cost?.toFixed(2)}
                  </p>
                  <p className="text-xs opacity-70">Per unit ({bomCost.currency}) · Method: {bomCost.cost_method?.replace('_', ' ')}</p>
                </div>
                <div>
                  <p className="overline mb-2">Component Breakdown</p>
                  <table className="w-full table-dense">
                    <thead>
                      <tr>
                        <th>Variant</th>
                        <th>Qty</th>
                        <th>Unit Cost</th>
                        <th>Line Cost</th>
                      </tr>
                    </thead>
                    <tbody>
                      {bomCost.breakdown?.map((b, i) => (
                        <tr key={i}>
                          <td className="font-mono text-xs">{b.variant_id?.slice(-8)}</td>
                          <td className="font-mono">{b.quantity}</td>
                          <td className="font-mono">${b.unit_cost?.toFixed(2)}</td>
                          <td className="font-mono font-bold">${b.line_cost?.toFixed(2)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </DialogContent>
        </Dialog>
      </div>

      {/* Production scan-to-count dialog — launched from each PO row's "Scan" button */}
      {scanPO && (
        <ProductionScanDialog
          po={scanPO}
          onChanged={fetchData}
          onClose={() => setScanPO(null)}
        />
      )}

      {/* Completion put-away dialog — pick a location for the finished goods */}
      <Dialog open={!!completePO} onOpenChange={(o) => { if (!o) setCompletePO(null); }}>
        <DialogContent className="max-w-lg" data-testid="complete-po-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <MapPin size={18} weight="fill" className="text-brand" />
              Put away finished goods
            </DialogTitle>
          </DialogHeader>
          {completePO && (
            <div className="space-y-4 text-sm">
              <div className="border border-border bg-muted/30 p-3">
                <p className="font-mono text-xs text-muted-foreground">{completePO.production_order_number}</p>
                <p className="font-bold mt-0.5">{completePO.finished_good_name} × {completePO.quantity}</p>
                <p className="text-xs text-muted-foreground mt-1">BOM: {completePO.bom_name}</p>
              </div>

              <div>
                <Label htmlFor="complete-location-search" className="text-xs uppercase tracking-widest text-muted-foreground">
                  Destination location
                </Label>
                <Input
                  id="complete-location-search"
                  placeholder="Search by code or zone…"
                  value={completeSearch}
                  onChange={(e) => setCompleteSearch(e.target.value)}
                  className="mt-2"
                  data-testid="complete-location-search"
                />
                <div className="mt-2 border border-border max-h-64 overflow-y-auto" data-testid="complete-location-list">
                  {locationsLoading ? (
                    <p className="text-xs text-muted-foreground p-3">Loading locations…</p>
                  ) : (
                    (() => {
                      const q = completeSearch.trim().toLowerCase();
                      const filtered = locations
                        .filter((l) => l.is_active !== false)
                        .filter((l) => !q || (l.location_code || '').toLowerCase().includes(q) || (l.zone || '').toLowerCase().includes(q))
                        .slice(0, 100);
                      if (filtered.length === 0) {
                        return <p className="text-xs text-muted-foreground p-3">No matching locations.</p>;
                      }
                      return filtered.map((l) => {
                        const active = completeLocationId === l.id;
                        return (
                          <button
                            type="button"
                            key={l.id}
                            onClick={() => setCompleteLocationId(l.id)}
                            className={`w-full text-left px-3 py-2 border-b border-border last:border-b-0 text-xs flex items-center justify-between gap-2 hover:bg-muted/40 ${active ? 'bg-brand/10 border-l-2 border-l-brand' : ''}`}
                            data-testid={`complete-location-option-${l.location_code}`}
                          >
                            <span className="font-mono font-bold">{l.location_code}</span>
                            <span className="text-muted-foreground">
                              {l.zone && <>Zone {l.zone}</>}
                              {typeof l.current_occupancy === 'number' && typeof l.capacity === 'number' && (
                                <> · {l.current_occupancy}/{l.capacity}</>
                              )}
                            </span>
                          </button>
                        );
                      });
                    })()
                  )}
                </div>
                {completeLocationId && (
                  <p className="mt-2 text-[11px] text-muted-foreground">
                    Finished goods will be created as available stock at{' '}
                    <span className="font-mono font-bold">
                      {locations.find((l) => l.id === completeLocationId)?.location_code}
                    </span>
                    .
                  </p>
                )}
              </div>
            </div>
          )}
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setCompletePO(null)} data-testid="complete-cancel-btn">
              Cancel
            </Button>
            <Button
              onClick={handleCompleteWithLocation}
              disabled={!completeLocationId || completing}
              className="bg-brand hover:bg-brand/90"
              data-testid="complete-confirm-btn"
            >
              <CheckCircle size={16} className="mr-2" weight="fill" />
              {completing ? 'Completing…' : 'Complete production'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Layout>
  );
}
